import React, { useState, useEffect } from 'react'
import OffToggle from '../Components/Images/OffToggle.png';
import OnToggle from '../Components/Images/OnToggle.png';

const InvoiceSettings = ({ TransactionType }) => {
    const [settings, setSettings] = useState({
        send_invoice_immediately: false,
        send_remider_for_invoices: false,
        nudge_duration: "3 days",
    });

    const [isDirty, setIsDirty] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [status, setStatus] = useState({ message: '', type: '' });

    useEffect(() => {
        fetch(`${process.env.REACT_APP_BACKEND_URL}/notifications`, {
            headers: { Authorization: `Token ${localStorage.getItem("token")}` }
        }).then(res => res.json()).then(data => setSettings(data));
    }, []);

    const toggleSetting = (key) => {
        setSettings((prev) => ({
            ...prev,
            [key]: !prev[key],
        }));
        setIsDirty(true);
    };

    const handleChange = (e) => {
        setSettings((prev) => ({
            ...prev,
            nudge_duration: e.target.value,
        }));
        setIsDirty(true);
    };

    const handleSave = async () => {
        setIsSubmitting(true);

        try {
            const token = localStorage.getItem("token");
            const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/update-notifications`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Token ${token}`,
                },
                body: JSON.stringify(settings),
            });

            const data = await res.json();
            if (!res.ok) throw new Error(data.message || 'Update failed');

            setStatus({ message: 'Invoice settings updated ✅', type: 'success' });
            setIsDirty(false);

        } catch (error) {
            console.error("Failed to save invoice settings:", error);
            setStatus({ message: error.message, type: 'error' });
        } finally {
            setIsSubmitting(false);
            setTimeout(() => setStatus({ message: '', type: '' }), 3000);
        }
    };

    return (
        <div className={TransactionType === 'Invoice' ? 'flex flex-col gap-10 lg:w-[90%]' : 'hidden'}>
            <div className='flex justify-between relative'>
                <div className='flex flex-col gap-1'>
                    <p className='text-[18px] font-medium text-[#1D1C1F]'>Invoice settings</p>
                    <p className='sp:text-[14px] text-[13px] font-normal text-[#525154]'>Update your invoice preferences</p>
                </div>
                <button
                    onClick={handleSave}
                    disabled={!isDirty || isSubmitting}
                    className={`text-[14px] font-medium px-4 py-[10px] rounded-lg ${!isDirty || isSubmitting
                        ? 'bg-[#E8E1F5] text-[#531CB3] cursor-not-allowed'
                        : 'bg-[#531CB3] text-white cursor-pointer'
                        }`}
                >
                    {isSubmitting ? 'Saving...' : 'Save changes'}
                </button>

                {status.message && (
                    <div className={status.type === 'error' ? 'bg-red-500 absolute top-3 left-1/2 -translate-x-1/2 text-white p-2 rounded-md flex items-center justify-between' : 'bg-green-500 absolute top-3 left-1/2 -translate-x-1/2 text-white p-2 rounded-md flex items-center justify-between'}>
                        {status.message}
                    </div>
                )}
            </div>

            <div className='flex sm:flex-row flex-col gap-5'>
                <div className='sm:w-[40%]'>
                    <p className='text-[14px] font-medium text-[#1D1C1F]'>Send automatically</p>
                </div>
                <div className='flex flex-col gap-2 sm:w-[70%]'>
                    <div className='flex items-center gap-2'>
                        <img
                            className='cursor-pointer'
                            src={settings.send_invoice_immediately ? OnToggle : OffToggle}
                            alt=""
                            onClick={() => toggleSetting("send_invoice_immediately")}
                        />
                        <p className='text-[16px] font-medium text-[#302F33]'>Send invoice immediately</p>
                    </div>
                    <div className='ml-[46px]'>
                        <p className='text-[#525154] text-[16px] font-normal'>
                            Send invoices immediately as they are created. Enabling this would skip drafts
                            and send your invoice directly to your customer. This will enable a 10 sec callback
                            in case you need to make adjustments. We've always got your back.
                        </p>
                    </div>
                </div>
            </div>

            <div className='flex sm:flex-row flex-col gap-5'>
                <div className='sm:w-[40%]'>
                    <p className='text-[14px] font-medium text-[#1D1C1F]'>Automated emails</p>
                </div>
                <div className='flex flex-col gap-2 sm:w-[70%]'>
                    <div className='flex items-center gap-2'>
                        <img
                            className='cursor-pointer'
                            src={settings.send_remider_for_invoices ? OnToggle : OffToggle}
                            alt=""
                            onClick={() => toggleSetting("send_remider_for_invoices")}
                        />
                        <p className='text-[16px] font-medium text-[#302F33]'>Send automated reminder for invoices</p>
                    </div>
                    <div className='ml-[46px]'>
                        <p className='text-[#525154] text-[16px] font-normal'>
                            Send your clients a nudge when they have not responded to your invoice after the due period.
                        </p>
                    </div>
                </div>
            </div>

            {settings.send_remider_for_invoices && (
                <div className='flex sm:flex-row flex-col sm:items-center gap-5'>
                    <div className='sm:w-[40%]'>
                        <p className='text-[14px] font-medium text-[#1D1C1F]'>Nudge amount</p>
                        <p className='text-[14px] font-normal text-[#525154]'>Select the duration for the automated emails.</p>
                    </div>
                    <select
                        className="rounded-lg py-[10px] sm:w-[70%] outline-none px-[14px] border border-[#D2D0D6] appearance-none bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2212%22%20height%3D%227%22%20viewBox%3D%220%200%2012%207%22%20fill%3D%22none%22%20xmlns%3D%22http://www.w3.org/2000/svg%22%3E%3Cpath%20d%3D%22M1%201L6%206L11%201%22%20stroke%3D%22%23666%22%20stroke-width%3D%222%22/%3E%3C/svg%3E')] bg-no-repeat bg-[right_14px_center]"
                        value={settings.nudge_duration}
                        onChange={handleChange}
                        required
                    >
                        <option value="3">3 days</option>
                        <option value="5">5 days</option>
                        <option value="10">10 days</option>
                    </select>
                </div>
            )}
        </div>
    )
}

export default InvoiceSettings
